import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import EnquiryPopup from '@/components/EnquiryPopup';
import {
  MapPin, Building2, ArrowLeft, Ruler, IndianRupee, Calendar,
  ChevronLeft, ChevronRight, Shield, Zap, Car, Droplets, Sun,
  Wifi, Check, Phone, Flame, Train, ShoppingBag, Hotel, Heart,
  BusFront, Building, FileText, ExternalLink, Play,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectDetailPage = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showEnquiry, setShowEnquiry] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await axios.get(`${API}/projects/${id}`);
        setProject(res.data);
      } catch (err) {
        // handle error
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-24 flex items-center justify-center h-[60vh]">
          <div className="animate-spin w-10 h-10 border-3 border-[#c9a84c] border-t-transparent rounded-full" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-24 text-center py-32">
          <Building2 className="w-16 h-16 text-[#8a7a50] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#2a1e00] mb-2">Project Not Found</h2>
          <Link to="/" className="text-[#c9a84c] hover:underline">Back to Home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    if (url.startsWith('http')) {
      try {
        const parsed = new URL(url);
        if (parsed.pathname.startsWith('/api/uploads')) {
          return `${process.env.REACT_APP_BACKEND_URL}${parsed.pathname}`;
        }
      } catch (e) {}
      return url;
    }
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const images = project.gallery_images?.length > 0
    ? project.gallery_images.map(resolveUrl)
    : project.thumbnail
      ? [resolveUrl(project.thumbnail)]
      : [];

  const boolFeatures = [
    { key: 'rainwater_harvesting', label: 'Rainwater Harvesting', icon: Droplets },
    { key: 'solar_panels', label: 'Solar Panels', icon: Sun },
    { key: 'ev_charging', label: 'EV Charging', icon: Zap },
    { key: 'cctv_security', label: 'CCTV / Security', icon: Shield },
    { key: 'wheelchair_accessible', label: 'Wheelchair Accessible', icon: Heart },
  ];

  const landmarks = [
    { key: 'nearby_corporates', label: 'Nearby Corporates', icon: Building },
    { key: 'nearby_malls', label: 'Nearby Malls', icon: ShoppingBag },
    { key: 'nearby_hospitals', label: 'Nearby Hospitals', icon: Heart },
    { key: 'nearby_hotels', label: 'Nearby Hotels (5-star)', icon: Hotel },
    { key: 'nearest_metro_station', label: 'Nearest Metro', icon: Train },
    { key: 'nearest_bus_stop', label: 'Nearest Bus Stop', icon: BusFront },
  ].filter(l => project[l.key]);

  return (
    <div className="min-h-screen bg-[#fdf9f0]" data-testid="project-detail-page">
      <Header />

      <div className="pt-20">
        {/* Back Nav */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/" className="inline-flex items-center text-sm text-[#6a5a30] hover:text-[#c9a84c] transition-colors" data-testid="back-link">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Link>
        </div>

        {/* Hero / Gallery */}
        {images.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
            <div className="relative rounded-2xl overflow-hidden h-[300px] md:h-[450px]">
              <img
                src={images[currentImageIndex]}
                alt={project.project_name}
                className="w-full h-full object-cover"
              />
              {images.length > 1 && (
                <>
                  <button onClick={() => setCurrentImageIndex(i => (i - 1 + images.length) % images.length)} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/40 text-white rounded-full flex items-center justify-center hover:bg-black/60">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button onClick={() => setCurrentImageIndex(i => (i + 1) % images.length)} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/40 text-white rounded-full flex items-center justify-center hover:bg-black/60">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((_, idx) => (
                      <button key={`dot-${idx}`} onClick={() => setCurrentImageIndex(idx)} className={`w-2.5 h-2.5 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-[#c9a84c]' : 'bg-white/50'}`} />
                    ))}
                  </div>
                </>
              )}
              {/* Badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                <span className="bg-[#c9a84c] text-white text-xs font-bold px-4 py-1.5 rounded-full">{project.project_status}</span>
                {project.is_hot_deal && <span className="bg-red-500 text-white text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1"><Flame className="w-3 h-3" /> Hot Deal</span>}
              </div>
              <div className="absolute top-4 right-4">
                <span className="bg-white/90 backdrop-blur-sm text-[#2a1e00] text-xs font-bold px-4 py-1.5 rounded-full">{project.project_type}</span>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Title Block */}
              <div className="bg-white rounded-2xl p-6 md:p-8">
                <p className="text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-1">{project.developer_name}</p>
                <h1 className="text-2xl md:text-3xl font-bold text-[#2a1e00] mb-3" data-testid="project-title">{project.project_name}</h1>
                {project.sector && (
                  <div className="flex items-center gap-2 text-[#6a5a30] mb-4">
                    <MapPin className="w-5 h-5 text-[#c9a84c]" />
                    <span>{project.sector}{project.micro_location ? `, ${project.micro_location}` : ''}</span>
                  </div>
                )}
                {project.full_address && <p className="text-sm text-[#8a7a50]">{project.full_address}</p>}
                {project.rera_number && (
                  <p className="text-xs text-[#8a7a50] mt-3 border-t border-[#fdf8ee] pt-3">RERA: {project.rera_number}</p>
                )}
              </div>

              {/* Key Highlights */}
              <div className="bg-white rounded-2xl p-6 md:p-8">
                <h2 className="text-lg font-bold text-[#2a1e00] mb-6">Key Highlights</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {project.total_floors && (
                    <div className="bg-[#fdf4e0] p-4 rounded-xl text-center">
                      <p className="text-2xl font-bold text-[#c9a84c]">{project.total_floors}</p>
                      <p className="text-xs text-[#6a5a30] mt-1">Total Floors</p>
                    </div>
                  )}
                  {project.total_towers && (
                    <div className="bg-[#fdf4e0] p-4 rounded-xl text-center">
                      <p className="text-2xl font-bold text-[#c9a84c]">{project.total_towers}</p>
                      <p className="text-xs text-[#6a5a30] mt-1">Towers</p>
                    </div>
                  )}
                  {project.total_units && (
                    <div className="bg-[#fdf4e0] p-4 rounded-xl text-center">
                      <p className="text-2xl font-bold text-[#c9a84c]">{project.total_units}</p>
                      <p className="text-xs text-[#6a5a30] mt-1">Total Units</p>
                    </div>
                  )}
                  {(project.size_min || project.size_max) && (
                    <div className="bg-[#fdf4e0] p-4 rounded-xl text-center">
                      <p className="text-lg font-bold text-[#c9a84c]">{project.size_min || '—'} - {project.size_max || '—'}</p>
                      <p className="text-xs text-[#6a5a30] mt-1">Sq.ft. Range</p>
                    </div>
                  )}
                </div>
                {project.unit_types?.length > 0 && (
                  <div className="mt-6">
                    <p className="text-sm font-medium text-[#2a1e00] mb-3">Unit Types</p>
                    <div className="flex flex-wrap gap-2">
                      {project.unit_types.map(t => <span key={t} className="text-xs bg-[#fdf4e0] text-[#c9a84c] px-3 py-1.5 rounded-full font-medium">{t}</span>)}
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              {(project.long_description || project.short_description) && (
                <div className="bg-white rounded-2xl p-6 md:p-8">
                  <h2 className="text-lg font-bold text-[#2a1e00] mb-4">About This Project</h2>
                  <p className="text-[#6a5a30] leading-relaxed whitespace-pre-line">{project.long_description || project.short_description}</p>
                </div>
              )}

              {/* Specifications */}
              {(project.building_grade || project.structure_type || project.hvac_system || project.power_backup || project.ceiling_height) && (
                <div className="bg-white rounded-2xl p-6 md:p-8">
                  <h2 className="text-lg font-bold text-[#2a1e00] mb-6">Specifications</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      ['Building Grade', project.building_grade],
                      ['Structure', project.structure_type],
                      ['HVAC', project.hvac_system],
                      ['Power Backup', project.power_backup],
                      ['Ceiling Height', project.ceiling_height ? `${project.ceiling_height} ft` : null],
                      ['Glazing', project.glazing_type],
                      ['Lifts', project.lift_info],
                      ['Power Supply', project.power_supply_kva ? `${project.power_supply_kva} KVA` : null],
                      ['Internet', project.internet_fibre],
                      ['Parking Ratio', project.parking_ratio],
                      ['Efficiency', project.efficiency_ratio],
                    ].filter(([, v]) => v && v !== 'None').map(([label, value]) => (
                      <div key={label} className="border border-[rgba(201,168,76,0.2)] rounded-xl p-4">
                        <p className="text-xs text-[#8a7a50] mb-1">{label}</p>
                        <p className="text-sm font-semibold text-[#2a1e00]">{value}</p>
                      </div>
                    ))}
                  </div>
                  {project.parking_type?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs text-[#8a7a50] mb-2">Parking Type</p>
                      <div className="flex gap-2">{project.parking_type.map(p => <span key={p} className="text-xs bg-[#fdf8ee] px-3 py-1 rounded-full">{p}</span>)}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Amenities */}
              {(project.common_amenities?.length > 0 || boolFeatures.some(f => project[f.key])) && (
                <div className="bg-white rounded-2xl p-6 md:p-8">
                  <h2 className="text-lg font-bold text-[#2a1e00] mb-6">Amenities & Features</h2>
                  {project.common_amenities?.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                      {project.common_amenities.map(a => (
                        <div key={a} className="flex items-center gap-2 p-3 bg-[#fdf9f0] rounded-lg">
                          <Check className="w-4 h-4 text-[#c9a84c]" /><span className="text-sm">{a}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {boolFeatures.filter(f => project[f.key]).map(f => {
                      const Icon = f.icon;
                      return (
                        <div key={f.key} className="flex items-center gap-2 p-3 bg-[#fdf4e0] rounded-lg">
                          <Icon className="w-4 h-4 text-[#c9a84c]" /><span className="text-sm font-medium text-[#c9a84c]">{f.label}</span>
                        </div>
                      );
                    })}
                  </div>
                  {project.green_certification && project.green_certification !== 'None' && (
                    <div className="mt-4 p-3 bg-[#fdf4e0] rounded-lg inline-flex items-center gap-2">
                      <Shield className="w-4 h-4 text-[#c9a84c]" />
                      <span className="text-sm font-medium text-[#c9a84c]">{project.green_certification} Certified</span>
                    </div>
                  )}
                  {project.fire_safety?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs text-[#8a7a50] mb-2">Fire Safety</p>
                      <div className="flex gap-2">{project.fire_safety.map(f => <span key={f} className="text-xs bg-[#fdf8ee] px-3 py-1 rounded-full">{f}</span>)}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Nearby Landmarks */}
              {landmarks.length > 0 && (
                <div className="bg-white rounded-2xl p-6 md:p-8">
                  <h2 className="text-lg font-bold text-[#2a1e00] mb-6">Nearby Landmarks</h2>
                  <div className="space-y-4">
                    {landmarks.map(l => {
                      const Icon = l.icon;
                      return (
                        <div key={l.key} className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-[#fdf4e0] rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-[#c9a84c]" />
                          </div>
                          <div>
                            <p className="text-xs text-[#8a7a50]">{l.label}</p>
                            <p className="text-sm text-[#2a1e00]">{project[l.key]}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Location */}
              {(project.nearby_highways?.length > 0 || project.distance_cyber_city || project.distance_airport || project.nearest_metro) && (
                <div className="bg-white rounded-2xl p-6 md:p-8">
                  <h2 className="text-lg font-bold text-[#2a1e00] mb-6">Connectivity</h2>
                  {project.nearby_highways?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-[#8a7a50] mb-2">Expressway / Highway Proximity</p>
                      <div className="flex flex-wrap gap-2">{project.nearby_highways.map(h => <span key={h} className="text-xs bg-[#fdf4e0] text-[#c9a84c] px-3 py-1.5 rounded-full font-medium">{h}</span>)}</div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {project.distance_cyber_city && (
                      <div className="border border-[rgba(201,168,76,0.2)] rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-[#c9a84c]">{project.distance_cyber_city} km</p>
                        <p className="text-xs text-[#8a7a50] mt-1">From Cyber City</p>
                      </div>
                    )}
                    {project.distance_airport && (
                      <div className="border border-[rgba(201,168,76,0.2)] rounded-xl p-4 text-center">
                        <p className="text-2xl font-bold text-[#c9a84c]">{project.distance_airport} km</p>
                        <p className="text-xs text-[#8a7a50] mt-1">From IGI Airport</p>
                      </div>
                    )}
                    {project.nearest_metro && (
                      <div className="border border-[rgba(201,168,76,0.2)] rounded-xl p-4 text-center">
                        <p className="text-sm font-bold text-[#c9a84c]">{project.nearest_metro}</p>
                        <p className="text-xs text-[#8a7a50] mt-1">Nearest Metro</p>
                      </div>
                    )}
                  </div>
                  {project.google_maps_link && (
                    <a href={project.google_maps_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 text-sm text-[#c9a84c] hover:underline">
                      <ExternalLink className="w-4 h-4" /> View on Google Maps
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Price Card */}
              <div className="bg-white rounded-2xl p-6 sticky top-24">
                <h3 className="text-lg font-bold text-[#2a1e00] mb-4">Pricing</h3>
                {project.starting_price && (
                  <div className="mb-4">
                    <p className="text-xs text-[#8a7a50]">Starting From</p>
                    <p className="text-2xl font-bold text-[#c9a84c]">{project.starting_price}</p>
                  </div>
                )}
                {project.price_per_sqft && (
                  <div className="mb-4">
                    <p className="text-xs text-[#8a7a50]">Price per sq.ft. (BSP)</p>
                    <p className="text-lg font-bold text-[#2a1e00]">₹{Number(project.price_per_sqft).toLocaleString('en-IN')}</p>
                  </div>
                )}
                <div className="text-xs text-[#6a5a30] mb-2">{project.lease_or_sale}</div>
                {project.lease_price_sqft && <p className="text-sm text-[#6a5a30] mb-2">Lease: ₹{project.lease_price_sqft}/sq.ft./month</p>}
                {project.maintenance_charge && <p className="text-sm text-[#6a5a30] mb-2">Maintenance: ₹{project.maintenance_charge}/sq.ft./month</p>}
                {project.cam_charges && <p className="text-sm text-[#6a5a30] mb-2">CAM: ₹{project.cam_charges}/sq.ft./month</p>}
                {project.assured_return_pct && (
                  <div className="bg-[#fdf4e0] p-3 rounded-lg mb-4">
                    <p className="text-sm font-semibold text-[#c9a84c]">Assured Return: {project.assured_return_pct}</p>
                    {project.assured_return_duration && <p className="text-xs text-[#6a5a30]">Duration: {project.assured_return_duration}</p>}
                  </div>
                )}
                {project.payment_plan && project.payment_plan !== 'None' && (
                  <p className="text-sm text-[#6a5a30] mb-4">Payment Plan: {project.payment_plan}</p>
                )}
                {project.bank_approvals?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-[#8a7a50] mb-2">Bank Approvals</p>
                    <div className="flex flex-wrap gap-1">{project.bank_approvals.map(b => <span key={b} className="text-[10px] bg-[#fdf8ee] px-2 py-1 rounded-full">{b}</span>)}</div>
                  </div>
                )}
                {project.possession_date && (
                  <div className="flex items-center gap-2 text-sm text-[#6a5a30] mb-4">
                    <Calendar className="w-4 h-4 text-[#c9a84c]" />
                    <span>Possession: {project.possession_date}</span>
                  </div>
                )}
                <Button className="w-full bg-[#c9a84c] hover:bg-[#8a6a10] text-white py-3" data-testid="enquire-btn" onClick={() => setShowEnquiry(true)}>
                    <Phone className="w-4 h-4 mr-2" /> {project.enquiry_cta_label || 'Get Best Price'}
                  </Button>
                {project.brochure_url && (
                  <a href={project.brochure_url} target="_blank" rel="noopener noreferrer" className="block mt-3">
                    <Button variant="outline" className="w-full" data-testid="brochure-btn">
                      <FileText className="w-4 h-4 mr-2" /> Download Brochure
                    </Button>
                  </a>
                )}
                {project.video_url && (
                  <a href={project.video_url} target="_blank" rel="noopener noreferrer" className="block mt-3">
                    <Button variant="outline" className="w-full">
                      <Play className="w-4 h-4 mr-2" /> Video Tour
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <EnquiryPopup open={showEnquiry} onClose={() => setShowEnquiry(false)} />
    </div>
  );
};

export default ProjectDetailPage;

